import { Select, TextInput } from '@mantine/core';

// Mirrors clinical-api's dermoscopy-exception reason enum. Needed when a
// lesion has only a macroscopic processed image; supplying it lets the
// trigger succeed instead of 422ing.
export const DERMOSCOPY_EXCEPTION_OPTIONS = [
  { value: 'image_quality', label: 'Image quality' },
  { value: 'not_captured', label: 'Not captured' },
  { value: 'clinical_judgement', label: 'Clinical judgement' },
  { value: 'other', label: 'Other' },
];

interface Props {
  reason: string | null;
  note: string;
  onReasonChange: (value: string | null) => void;
  onNoteChange: (value: string) => void;
  noteRequiredButMissing: boolean;
  /**
   * When true, the reason selector shows a "required" error. Used by the
   * per-lesion trigger for a macroscopic-only lesion, where an exception reason
   * is mandatory. Defaults to false so the case-level card usage is unaffected.
   */
  reasonRequiredButMissing?: boolean;
  disabled?: boolean;
}

/**
 * The dermoscopy-exception reason selector + conditional "other" note input,
 * shared by the case-level run-all trigger and the per-lesion trigger in
 * CaseFindingsSection. A note is required when the
 * reason is "other".
 */
export function DermoscopyExceptionFields({
  reason,
  note,
  onReasonChange,
  onNoteChange,
  noteRequiredButMissing,
  reasonRequiredButMissing = false,
  disabled,
}: Props) {
  return (
    <>
      <Select
        label="Dermoscopy exception"
        placeholder="Dermoscopy exception reason (only needed for macroscopic-only lesions)"
        data={DERMOSCOPY_EXCEPTION_OPTIONS}
        value={reason}
        onChange={onReasonChange}
        error={
          reasonRequiredButMissing
            ? 'A dermoscopy exception reason is required for a macroscopic-only lesion.'
            : null
        }
        clearable
        disabled={disabled}
      />
      {reason === 'other' && (
        <TextInput
          label="Exception note"
          placeholder="Explain the dermoscopy exception"
          required
          value={note}
          onChange={(e) => onNoteChange(e.currentTarget.value)}
          error={noteRequiredButMissing ? 'A note is required when the reason is "Other".' : null}
          disabled={disabled}
        />
      )}
    </>
  );
}
